import { router, usePage } from "@inertiajs/react";
import { Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

interface SearchInputProps {
  initialValue?: string;
  placeholder?: string;
}

export function SearchInput({ initialValue = "", placeholder = "Search..." }: SearchInputProps) {
  const { url } = usePage();
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const performSearch = useCallback((searchValue: string) => {
    const params = new URLSearchParams(url.split("?")[1] || "");
    if (searchValue) {
      params.set("search", searchValue);
    } else {
      params.delete("search");
    }
    params.delete("page");
    router.get(url.split("?")[0], Object.fromEntries(params), {
      preserveState: true,
      preserveScroll: true,
    });
  }, [url]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (value !== initialValue) {
        performSearch(value);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [value, initialValue, performSearch]);

  const handleClear = () => {
    setValue("");
    performSearch("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && value) {
      handleClear();
    }
  };

  return (
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="pl-9 pr-9"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title="Clear (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
